import { TwitterShareButton, TwitterIcon, LineShareButton, LineIcon } from 'react-share';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { formatAmount, formatCount, formatDate, formatRound } from "../../../utils/common/format";
import { useNotification } from "../../../hooks/useNotification";
import PageMeta from "../../common/PageMeta";
import NoData from "../../common/NoData";
import { useEffect, useRef, useState } from "react";

const MIN_LOADING_MS = 500;

export default function LotteryNumlotIndexDetailTemplate({
  data,
  config,
  loading,
  showShare = true,
}) {
  const { notify } = useNotification();
  const [showLoading, setShowLoading] = useState(loading);
  const loadingStartedAt = useRef(Date.now());
  const totalPrize = data?.payouts?.reduce((sum, p) => sum + ((p.amount ?? 0) * (p.winners ?? 0)), 0) ?? null;
  
  const totalWinners = data?.payouts?.reduce((sum, p) => sum + (p.winners ?? 0), 0) ?? null;
  const salesCount = data?.sales_amount && config?.price ? Math.floor(data.sales_amount / config.price) : null;

  useEffect(() => {
    let timer;

    if (loading) {
      loadingStartedAt.current = Date.now();
      setShowLoading(true);
    } else {
      const elapsed = Date.now() - loadingStartedAt.current;
      const remaining = Math.max(0, MIN_LOADING_MS - elapsed);

      timer = setTimeout(() => {
        setShowLoading(false);
      }, remaining);
    }

    return () => clearTimeout(timer);
  }, [loading]);

  if (showLoading) {
    return (
      <div className="py-4 space-y-2 animate-pulse">
        <div className="h-4 bg-primary-500/30 rounded w-3/4" />
        <div className="h-4 bg-primary-500/20 rounded w-1/2" />
        <div className="h-4 bg-primary-500/10 rounded w-2/3" />
      </div>
    );
  }
  if (!data) {
    return (
      <>
        <PageMeta
          title="データが見つかりません"
          description="該当するデータが見つかりませんでした。"
          noindex
        />
        <NoData />
      </>
    );
  }

  const detailUrl = `${window.location.origin}${config?.path}detail/${data.round}`;
  const shareText = `${config?.label} ${formatRound(data.round)}（${formatDate(data.draw_date)}）の当選番号は ${data.main_number}${data.bonus_number ? `（ボーナス: ${data.bonus_number}）` : ""} でした。`;

  // 払戻金・当選口数・販売実績のいずれも記録がない回（主に2008年頃以前の古い回）
  const hasPayoutData =
    (data.payouts || []).some((p) => p.amount != null || p.winners != null) ||
    data.sales_amount != null ||
    (data.carryover != null && data.carryover > 0);

  if (!hasPayoutData) {
    return (
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] sm:text-xs md:text-sm text-primary-500/60">
          詳細データがありません
        </p>
        <a
          href={detailUrl}
          className="shrink-0 px-3 py-1.5 border border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-base-50 rounded-lg text-xs font-bold transition-colors"
        >
          詳細
        </a>
      </div>
    );
  }

  return (
    <div className="pb-4">

      {/* 払戻テーブル */}
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] sm:text-xs md:text-sm border border-primary-500">
          <thead>
            <tr className="text-primary-500 border-b border-primary-500 text-left"
              style={{
                backgroundColor: '#fdfbf7',
                backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
                backgroundPosition: '2px 0, 6px 2px',
                backgroundSize: '8px 4px',
              }}
            >
              <th className="px-2 md:px-4 py-2 font-bold whitespace-nowrap">［ 等級 ］</th>
              <th className="px-2 md:px-4 py-2 font-bold text-right whitespace-nowrap">［ 当選口数 ］</th>
              <th className="px-2 md:px-4 py-2 font-bold text-right whitespace-nowrap">［ 払戻金 ］</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dashed divide-primary-500">
            {(data.payouts || []).map((payout, i) => (
              <tr key={i}>
                <td className="px-2 md:px-4 py-2 text-primary-500">
                  {payout.name ? payout.name : `［ ${payout.grade}等 ］`}
                </td>
                <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">{formatCount(payout.winners)}</td>
                <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                  {formatAmount(payout.amount)}
                </td>
              </tr>
            ))}
            {data.carryover != null && data.carryover > 0 && (
              <tr>
                <td className="px-2 md:px-4 py-2 text-primary-500">［ キャリーオーバー ］</td>
                <td colSpan={2} className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                  {formatAmount(data.carryover)}
                </td>
              </tr>
            )}
          </tbody>

              <tfoot>
                <tr className="text-primary-500 border-y border-primary-500 text-left"
                  style={{
                    backgroundColor: '#fdfbf7',
                    backgroundImage: 'radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px), radial-gradient(circle, rgba(227,0,127,0.2) 1px, transparent 1px)',
                    backgroundPosition: '2px 0, 6px 2px',
                    backgroundSize: '8px 4px',
                  }}
                >
                  <th className="px-2 md:px-4 py-2 font-bold">［ 小計 ］</th>
                  <th className="px-2 md:px-4 py-2 font-bold text-right">［ 総数 ］</th>
                  <th className="px-2 md:px-4 py-2 font-bold text-right">［ 総額 ］</th>
                </tr>
                {totalPrize != null && totalPrize > 0 && (
                  <tr className="border-t border-primary-500">
                    <td className="px-2 md:px-4 py-2 text-primary-500">［ 総合払戻 ］</td>
                    <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                      {totalWinners != null ? `${totalWinners.toLocaleString()}口` : ''}
                    </td>
                    <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                      {formatAmount(totalPrize)}
                    </td>
                  </tr>
                )}
                {data.sales_amount != null && (
                  <tr className="border-t border-primary-500">
                    <td className="px-2 md:px-4 py-2 text-primary-500">［ 販売実績 ］</td>
                    <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                      {salesCount != null ? `${salesCount.toLocaleString()}枚` : ''}
                    </td>
                    <td className="px-2 md:px-4 py-2 text-right font-bold text-primary-500">
                      {formatAmount(data.sales_amount)}
                    </td>
                  </tr>
                )}
              </tfoot>
        </table>
        <p className="mt-2 text-[10px] text-primary-500/50 text-right">
          ※データの正確性は保証しません。必ず公式サイトでご確認ください。
        </p>
      </div>

      {/* ボタン群 */}
      <div className={`flex items-center mt-4 ${showShare ? "justify-between" : "justify-end"}`}>
        {/* 左：SNSボタン */}
        {showShare && (
          <div className="flex items-center gap-2">
            <TwitterShareButton url={detailUrl} title={shareText}>
              <TwitterIcon size={32} round bgStyle={{ fill: '#e3007f' }} iconFillColor='#fdfbf7' className='hover:opacity-50' />
            </TwitterShareButton>

            <LineShareButton url={detailUrl} title={shareText}>
              <LineIcon size={32} round bgStyle={{ fill: '#e3007f' }} iconFillColor='#fdfbf7' className='hover:opacity-50' />
            </LineShareButton>

            <CopyToClipboard text={detailUrl} onCopy={() => notify("URLをコピーしました")}>
              <button className="w-8 h-8 flex items-center justify-center bg-primary-500 text-base-50 rounded-full text-xs font-bold transition-colors hover:opacity-50">
                URL
              </button>
            </CopyToClipboard>
          </div>
        )}

        {/* 右：詳細リンク */}
        <a href={detailUrl}
          className="px-4 py-2 border border-primary-500 hover:bg-primary-500 text-primary-500 hover:text-base-50 rounded-lg text-sm font-bold transition-colors"
        >
          詳細
        </a>
      </div>
    </div>
  );
}